import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Pencil, RotateCcw } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import { overrideTradingLimitsIntradayAlgoTradingLimitsPut as overrideTradingLimits } from "@/client-admin/services.gen";
import { TradingLimits_Output } from "@/client-admin/types.gen";
import { cn, getErrorDetails } from "@/lib/utils";
import {
  formatTradingLimit,
  OPTIONAL_TRADING_LIMIT_FIELDS,
  sameTradingLimit,
  TRADING_LIMIT_FIELDS,
  TRADING_LIMIT_GROUPS,
  TRADING_LIMIT_UNITS,
  TradingLimitField,
  tradingLimitLabel,
} from "./tradingLimits";

// Mirrors the backend: DECIMAL(12, 2) ≥ 0, whole minutes ≥ 0.
const decimalLimit = z
  .string()
  .trim()
  .regex(/^\d{1,10}(\.\d{1,2})?$/, "A number ≥ 0 with at most 2 decimals");

const overrideTradingLimitsSchema = z.object({
  max_trade_volume_mwh_per_ptu: decimalLimit,
  max_hourly_volume_mwh: decimalLimit,
  max_daily_volume_mwh: decimalLimit,
  day_notional_cap_eur: decimalLimit,
  // null turns the check off for the day
  price_delta_eur: decimalLimit.nullable(),
  price_delta_percent: decimalLimit.nullable(),
  minutes_to_closed_id: z
    .string()
    .trim()
    .regex(/^\d{1,6}$/, "A whole number of minutes ≥ 0"),
});

type OverrideTradingLimitsFormValues = z.infer<typeof overrideTradingLimitsSchema>;

const toFormValues = (limits: TradingLimits_Output): OverrideTradingLimitsFormValues => ({
  ...limits,
  minutes_to_closed_id: String(limits.minutes_to_closed_id),
});

interface OverrideTradingLimitsDialogProps {
  deliveryDate: string;
  effective: TradingLimits_Output;
  defaults: TradingLimits_Output;
  canOverride: boolean;
}

export function OverrideTradingLimitsDialog({
  deliveryDate,
  effective,
  defaults,
  canOverride,
}: OverrideTradingLimitsDialogProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<OverrideTradingLimitsFormValues>({
    resolver: zodResolver(overrideTradingLimitsSchema),
    defaultValues: toFormValues(effective),
  });

  const { mutateAsync: overrideTradingLimitsMutation, isPending } = useMutation({
    mutationFn: overrideTradingLimits,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["intraday-algo-trading-limits", deliveryDate] });
      toast({
        title: "Override saved",
        description: `The new limits apply to ${deliveryDate}. An email notification has been sent.`,
      });
      setOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: getErrorDetails(error, true),
        variant: "destructive",
      });
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (isPending) return;
    if (nextOpen) form.reset(toFormValues(effective));
    setOpen(nextOpen);
  };

  const values = form.watch();
  const changedFields = TRADING_LIMIT_FIELDS.filter((field) => !sameTradingLimit(values[field], effective[field]));

  const handleSubmit = async (values: OverrideTradingLimitsFormValues) => {
    await overrideTradingLimitsMutation({
      requestBody: {
        delivery_date: deliveryDate,
        limits: { ...values, minutes_to_closed_id: Number(values.minutes_to_closed_id) },
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={!canOverride}
          title={canOverride ? undefined : "Limits of a past delivery date cannot be overridden"}
        >
          <Pencil className="mr-1 h-4 w-4" />
          Override limits
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>Override limits for {deliveryDate}</DialogTitle>
          <DialogDescription>Other dates keep the client&apos;s defaults. Saving emails the changes.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {TRADING_LIMIT_GROUPS.map((group) => (
              <fieldset key={group.name}>
                <legend className="mb-1 text-xs font-semibold text-muted-foreground">{group.name}</legend>
                <div className="divide-y">
                  {group.fields.map((limitField) => (
                    <LimitField
                      key={limitField}
                      limitField={limitField}
                      form={form}
                      effective={effective}
                      defaults={defaults}
                      changed={changedFields.includes(limitField)}
                    />
                  ))}
                </div>
              </fieldset>
            ))}

            <ChangeSummary changedFields={changedFields} values={values} effective={effective} />

            <DialogFooter className="gap-2 sm:justify-between">
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={() => form.reset(toFormValues(defaults))}
                title="Fill every field with the client's default limits; nothing is saved yet"
              >
                <RotateCcw className="mr-1 h-4 w-4" />
                Fill defaults
              </Button>
              <Button type="submit" disabled={isPending || changedFields.length === 0}>
                {isPending ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save override"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

interface LimitFieldProps {
  limitField: TradingLimitField;
  form: ReturnType<typeof useForm<OverrideTradingLimitsFormValues>>;
  effective: TradingLimits_Output;
  defaults: TradingLimits_Output;
  changed: boolean;
}

function LimitField({ limitField, form, effective, defaults, changed }: LimitFieldProps) {
  const optional = OPTIONAL_TRADING_LIMIT_FIELDS.includes(limitField);
  const unit = TRADING_LIMIT_UNITS[limitField];
  const label = tradingLimitLabel(limitField);
  return (
    <FormField
      control={form.control}
      name={limitField}
      render={({ field }) => {
        const off = field.value === null;
        return (
          <FormItem className="space-y-0 py-1.5">
            <div className="grid grid-cols-[1fr_2.75rem_8rem_4.5rem] items-center gap-x-3 sm:grid-cols-[1fr_2.75rem_8rem_4.5rem_7.5rem]">
              <FormLabel className={cn("font-normal", changed && "font-medium text-primary")}>{label}</FormLabel>
              <div>
                {optional && (
                  <Switch
                    checked={!off}
                    aria-label={`${label} check`}
                    onCheckedChange={(checked) =>
                      // Turning a check back on starts from the effective value, else the default.
                      field.onChange(checked ? String(effective[limitField] ?? defaults[limitField] ?? "") : null)
                    }
                  />
                )}
              </div>
              <FormControl>
                <Input
                  inputMode="decimal"
                  autoComplete="off"
                  aria-label={label}
                  {...field}
                  value={field.value ?? ""}
                  disabled={off}
                  placeholder={off ? "Off" : undefined}
                  className={cn("h-9 text-right tabular-nums", changed && "border-primary")}
                />
              </FormControl>
              <span className="text-sm text-muted-foreground">{unit}</span>
              <span className="hidden text-right text-xs tabular-nums text-muted-foreground sm:block">
                Default {formatTradingLimit(limitField, defaults[limitField])}
              </span>
            </div>
            <FormMessage className="pt-1 text-right" />
          </FormItem>
        );
      }}
    />
  );
}

interface ChangeSummaryProps {
  changedFields: TradingLimitField[];
  values: OverrideTradingLimitsFormValues;
  effective: TradingLimits_Output;
}

/** What the override email will list: current value → new value per changed limit. */
function ChangeSummary({ changedFields, values, effective }: ChangeSummaryProps) {
  if (changedFields.length === 0) {
    return <p className="text-sm text-muted-foreground">No changes yet. Edit a limit to save an override.</p>;
  }
  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
      <p className="mb-1 font-medium">
        {changedFields.length} {changedFields.length === 1 ? "change" : "changes"}
      </p>
      <ul className="space-y-0.5">
        {changedFields.map((field) => (
          <li key={field} className="flex justify-between gap-4">
            <span>{tradingLimitLabel(field)}</span>
            <span className="whitespace-nowrap tabular-nums">
              {formatTradingLimit(field, effective[field])} →{" "}
              <strong>{formatTradingLimit(field, values[field])}</strong>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
